import React from 'react';
import ReactDOM from 'react-dom/client';
import { resolveCampaign } from './compactCampaignLanding/campaignRegistry';
import { setCampaignContext } from './compactCampaignLanding/compactCampaignState';
import { fastCampaignLoader } from './fastCampaignLoader';
import { fastRouteLoader } from './fastRouteLoader';
import {
	initDemoExperienceFromCookie,
	getCurrentCampaignCode as getPersistedDemoCampaignCode,
} from './lib/DemoExperienceManager';
import { reportAppUsage } from './compactCampaignLanding/AppUsageReporter';
import { Clarity } from './Infrastructure/Clarity';

const rootElement = document.getElementById('root');

if (!rootElement) {
	throw new Error('Root element not found');
}

// Initialize Microsoft Clarity
Clarity.init();

const root = ReactDOM.createRoot(rootElement);
const render = (node: React.ReactNode) => root.render(<React.StrictMode>{node}</React.StrictMode>);

initDemoExperienceFromCookie();

// 1. Check for dedicated fast routes first
if (window.location.pathname === '/schedule') {
	reportAppUsage('application.entry-point', {
		customContext: { key: 'entry-route', value: '/schedule' },
	});
	void fastRouteLoader({
		render,
		loader: () => import('./fastRoutes/schedule/ScheduleLanding'),
	});
} else if (window.location.pathname === '/call-scheduler-app/success') {
	reportAppUsage('application.entry-point', {
		customContext: { key: 'entry-route', value: '/call-scheduler-app/success' },
	});
	void fastRouteLoader({
		render,
		loader: () => import('./fastRoutes/schedule/ResultPages/SuccessPage'),
	});
} else if (window.location.pathname === '/call-scheduler-app/cancel') {
	reportAppUsage('application.entry-point', {
		customContext: { key: 'entry-route', value: '/call-scheduler-app/cancel' },
	});
	void fastRouteLoader({
		render,
		loader: () => import('./fastRoutes/schedule/ResultPages/CancelPage'),
	});
} else if (window.location.pathname === '/call-scheduler-app/retry') {
	reportAppUsage('application.entry-point', {
		customContext: { key: 'entry-route', value: '/call-scheduler-app/retry' },
	});
	void fastRouteLoader({
		render,
		loader: () => import('./fastRoutes/schedule/ResultPages/RetryPage'),
	});
} else {
    // 2. Fallback to Campaign Logic or Main App
    const searchParams = new URLSearchParams(window.location.search);
    const campaignCode = searchParams.get('campaign') ?? searchParams.get('c');
    
    // campaignLanding query param is controlled by compact landing buttons.
    // When campaignLanding=skip we force the regular app shell to render even if the
    // campaign code resolves; otherwise we rely on the persisted demo experience context
    // to decide whether the landing should appear again.
    const campaignLandingMode = searchParams.get('campaignLanding');
    const resolvedCampaign = resolveCampaign(campaignCode);
    const persistedDemoCampaignCode = getPersistedDemoCampaignCode();
    const shouldRenderCampaignLanding = Boolean(
        resolvedCampaign &&
        campaignLandingMode !== 'skip' &&
        (campaignLandingMode === 'force' || persistedDemoCampaignCode !== resolvedCampaign.code),
    );
    setCampaignContext(resolvedCampaign?.code ?? null, resolvedCampaign?.defaultLanguage ?? null);

    void fastCampaignLoader({ render, resolvedCampaign, shouldRenderCampaignLanding });
}


// Register service worker only in production; in dev, ensure no SW/caches interfere with HMR
if ('serviceWorker' in navigator) {
	if (import.meta.env.PROD) {
		window.addEventListener('load', () => {
			navigator.serviceWorker.register('/sw.js').catch(() => {
				// ignore registration errors
			});
		});
	} else {
		// Dev: unregister any existing service workers and clear caches to avoid stale modules
		navigator.serviceWorker.getRegistrations().then((regs) => {
			for (const reg of regs) reg.unregister();
		});
		if ('caches' in window) {
			caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
		}
	}
}
